import React, { useState } from 'react';
import { brandColors, styles } from '../styles/clientManagementStyles';
import { SECONDARY_MUSCLES, ALL_SECONDARY_MUSCLES } from '../constants/exerciseCategories';
import { shallowEqualIgnoringFunctions } from '../utils/memoCompare';
import { SWAP_HISTORY_DAYS } from '../services/swapService';
import ClientSwapsPanel from './ClientSwapsPanel';
import ExerciseDemo from './ui/ExerciseDemo';
import { colors as theme } from '../theme';
import AddDayPanel from './AddDayPanel';
import SaveOnBlurInput from './SaveOnBlurInput';

const ClientProgramsTab = ({
  selectedClient,
  programs,
  exerciseLibrary,
  assigningTo,
  selectedProgram,
  setSelectedProgram,
  editingProgram,
  setEditingProgram,
  programDetails,
  setProgramDetails,
  swappingExercise,
  setSwappingExercise,
  pendingSwapEx,
  setPendingSwapEx,
  swapReason,
  setSwapReason,
  setConfirmDialog,
  showOverflowMenu,
  setShowOverflowMenu,
  programHistory,
  showAssignPanel,
  setShowAssignPanel,
  showAdvanceWeek,
  setShowAdvanceWeek,
  advanceWeekTarget,
  setAdvanceWeekTarget,
  advancingWeek,
  availableWeeks,
  generatingNextWeek,
  loadingProgramHistory,
  exerciseAvgs,
  setExerciseAvgs,
  swapSearch,
  setSwapSearch,
  addingExercise,
  setAddingExercise,
  addExerciseSearch,
  setAddExerciseSearch,
  showSwapAll,
  setShowSwapAll,
  clientFlags,
  clientSwaps,
  resolvingFlag,
  setResolvingFlag,
  resolveNote,
  setResolveNote,
  flagSubSearch,
  setFlagSubSearch,
  flagSubSelected,
  setFlagSubSelected,
  flagSubDropdownOpen,
  setFlagSubDropdownOpen,
  showDeleteSession,
  setShowDeleteSession,
  clientSessions,
  loadingSessions,
  deletingSession,
  movingSession,
  programDays,
  moveTarget,
  setMoveTarget,
  handleResolveFlag,
  markAsReviewed,
  viewProgram,
  openAdvanceWeek,
  loadClientSessions,
  loadProgramDays,
  handleDeleteSession,
  handleMoveSession,
  handleAdvanceWeek,
  handleGenerateNextWeek,
  getSuggestedExercises,
  programLoadFailed = false,
  editorWeek = { week: null, note: null },
  onViewWeek,
  addDay,
  addDaySaving = false,
  retryProgramLoad,
  updateBlockItem,
  updateBlockRest,
  swapBlockExercise,
  swapSaving,
  deleteBlockItem,
  updateExercise,
  swapExercise,
  deleteProgramExercise,
  addExerciseToDay,
  addExerciseSaving,
  assignProgram,
  getAssignableProgramGroups,
}) => {
  const [swapCategoryFilter, setSwapCategoryFilter] = useState('All');
  const [swapSecondaryFilter, setSwapSecondaryFilter] = useState('All');
  const [swapHoveredId, setSwapHoveredId] = useState(null);

  const getSwapMovementCategories = () => {
    const categories = new Set(exerciseLibrary.map(e => e.movement_category).filter(Boolean));
    return ['All', ...Array.from(categories).sort()];
  };

  const getSwapSecondaryMuscles = () => {
    if (swapCategoryFilter === 'All') return ['All', ...ALL_SECONDARY_MUSCLES];
    return ['All', ...(SECONDARY_MUSCLES[swapCategoryFilter] || [])];
  };

  const resetSwapFilters = () => {
    setSwapCategoryFilter('All');
    setSwapSecondaryFilter('All');
  };

  return (
    <div style={styles.section}>
      {(() => {
        const activeAssignment = programHistory.find(a => a.is_active);
        const activeProgram = activeAssignment
          ? (activeAssignment.program || { id: activeAssignment.program_id, name: 'Assigned Program', description: '' })
          : null;
        const pastAssignments = programHistory.filter(a => !a.is_active);

        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

        return loadingProgramHistory ? (
          <div style={{ textAlign: 'center', padding: '48px', color: brandColors.silver }}>Loading programs...</div>
        ) : (
          <>
            {/* ── Exercise Flags ── */}
            <div style={{ ...styles.card, padding: '20px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span style={{ color: brandColors.red, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' }}>🚩 Exercise Flags</span>
                {clientFlags.length > 0 && (
                  <span style={{ background: brandColors.red, color: brandColors.white, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                    {clientFlags.length} unresolved
                  </span>
                )}
              </div>

              {clientFlags.length === 0 ? (
                <p style={{ color: brandColors.silver, fontSize: 13, margin: 0 }}>No flagged exercises</p>
              ) : (
                clientFlags.map(flag => (
                  <div key={flag.id} style={{
                    background: brandColors.darkGray,
                    border: `1px solid ${brandColors.red}`,
                    borderRadius: 8,
                    padding: 14,
                    marginBottom: 10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ color: brandColors.white, fontWeight: 600, fontSize: 14, margin: 0 }}>{flag.exercise_name}</p>
                        {flag.notes && <p style={{ color: brandColors.warning, fontSize: 13, margin: '4px 0 0' }}>"{flag.notes}"</p>}
                        <p style={{ color: brandColors.silver, fontSize: 11, margin: '4px 0 0' }}>
                          {new Date(flag.flagged_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => { setResolvingFlag(flag.id); setResolveNote(''); setFlagSubSelected(null); setFlagSubSearch(''); setFlagSubDropdownOpen(false); }}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${brandColors.green}`,
                          color: brandColors.green,
                          borderRadius: 8,
                          padding: '6px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        Resolve
                      </button>
                    </div>
                    {resolvingFlag === flag.id && (
                      <div style={{ marginTop: 12, borderTop: `1px solid ${brandColors.lightGray}`, paddingTop: 12 }}>

                        {/* Substitute exercise picker */}
                        <div style={{ marginBottom: 10, position: 'relative' }}>
                          <div style={{ fontSize: 11, color: brandColors.silver, marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Alternative Exercise (optional)
                          </div>
                          {flagSubSelected ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(212,175,55,0.1)', border: `1px solid ${brandColors.gold}`, borderRadius: 8, padding: '7px 10px' }}>
                              <span style={{ color: brandColors.gold, fontSize: 13, fontWeight: 600, flex: 1 }}>{flagSubSelected.name}</span>
                              <button onClick={() => { setFlagSubSelected(null); setFlagSubSearch(''); }} style={{ background: 'none', border: 'none', color: brandColors.silver, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>✕</button>
                            </div>
                          ) : (() => {
                            // Find flagged exercise's muscle category for sorting
                            const flaggedEx = exerciseLibrary.find(e => e.id === flag.exercise_id);
                            const flaggedCategory = flaggedEx?.movement_category || null;

                            const filtered = exerciseLibrary
                              .filter(ex => ex.id !== flag.exercise_id && ex.name.toLowerCase().includes(flagSubSearch.toLowerCase()));

                            const sameGroup = filtered.filter(ex => flaggedCategory && ex.movement_category === flaggedCategory);
                            const otherGroup = filtered.filter(ex => !flaggedCategory || ex.movement_category !== flaggedCategory);

                            const renderRow = (ex) => (
                              <div
                                key={ex.id}
                                onMouseDown={() => { setFlagSubSelected(ex); setFlagSubSearch(''); setFlagSubDropdownOpen(false); }}
                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: brandColors.white, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.12)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <span>{ex.name}</span>
                                {ex.movement_category && <span style={{ fontSize: 11, color: brandColors.gold, background: 'rgba(212,175,55,0.15)', borderRadius: 8, padding: '1px 6px', flexShrink: 0, marginLeft: 6 }}>{ex.movement_category}</span>}
                              </div>
                            );

                            return (
                              <div>
                                <input
                                  value={flagSubSearch}
                                  onChange={e => { setFlagSubSearch(e.target.value); setFlagSubDropdownOpen(true); }}
                                  onFocus={() => setFlagSubDropdownOpen(true)}
                                  onBlur={() => setTimeout(() => setFlagSubDropdownOpen(false), 150)}
                                  placeholder="Search or browse exercises..."
                                  style={{ width: '100%', background: brandColors.mediumGray, border: `1px solid ${flagSubDropdownOpen ? brandColors.gold : brandColors.lightGray}`, borderRadius: flagSubDropdownOpen ? '8px 8px 0 0' : 8, color: brandColors.white, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
                                />
                                {flagSubDropdownOpen && (
                                  <div style={{ position: 'absolute', zIndex: 400, left: 0, right: 0, maxHeight: 260, overflowY: 'auto', background: brandColors.mediumGray, border: `1px solid ${brandColors.gold}`, borderTop: 'none', borderRadius: '0 0 8px 8px', boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
                                    {sameGroup.length === 0 && otherGroup.length === 0 && (
                                      <div style={{ padding: '12px', color: brandColors.silver, fontSize: 12, textAlign: 'center' }}>No exercises found</div>
                                    )}
                                    {sameGroup.length > 0 && (
                                      <>
                                        <div style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, color: brandColors.gold, background: 'rgba(212,175,55,0.08)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                          Same Muscle Group — {flaggedCategory}
                                        </div>
                                        {sameGroup.map(renderRow)}
                                      </>
                                    )}
                                    {otherGroup.length > 0 && (
                                      <>
                                        <div style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, color: brandColors.silver, background: 'rgba(255,255,255,0.04)', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: sameGroup.length > 0 ? `1px solid ${brandColors.lightGray}` : 'none' }}>
                                          Other Exercises
                                        </div>
                                        {otherGroup.map(renderRow)}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Note to client */}
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: brandColors.silver, marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Note to Client <span style={{ color: brandColors.gold, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(visible in their workout)</span>
                          </div>
                          <input
                            value={resolveNote}
                            onChange={e => setResolveNote(e.target.value)}
                            placeholder="e.g. Use cable fly instead, keep reps slow..."
                            style={{ width: '100%', background: brandColors.mediumGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: 8, color: brandColors.white, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
                          />
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => { setResolvingFlag(null); setFlagSubSelected(null); setFlagSubSearch(''); }}
                            style={{ flex: 1, background: 'transparent', border: `1px solid ${brandColors.lightGray}`, color: brandColors.silver, borderRadius: 8, padding: 8, cursor: 'pointer', fontSize: 13 }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleResolveFlag(flag.id)}
                            style={{ flex: 2, background: brandColors.green, border: 'none', color: brandColors.white, borderRadius: 8, padding: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                          >
                            {flagSubSelected ? '✓ Assign & Resolve' : '✓ Mark Resolved'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* ── Client swaps ── */}
            <ClientSwapsPanel clientSwaps={clientSwaps} />

            {/* ── New client review banner ── */}
            {selectedClient.needs_review && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '14px 18px',
                marginBottom: '20px',
                backgroundColor: 'rgba(212,175,55,0.08)',
                border: '1px solid rgba(212,175,55,0.4)',
                borderRadius: '8px',
              }}>
                <div>
                  <p style={{ color: theme.gold, fontWeight: 700, fontSize: '13px', margin: 0 }}>
                    🔔 New client — AI auto-assigned a program
                  </p>
                  <p style={{ color: theme.textMuted, fontSize: '12px', margin: '3px 0 0' }}>
                    Review the program below, make any adjustments, then mark as reviewed.
                  </p>
                </div>
                <button
                  onClick={() => markAsReviewed(selectedClient.user_id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: theme.gold,
                    color: theme.surface1,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Mark Reviewed
                </button>
              </div>
            )}

            {/* ── Active Program ── */}
            {activeProgram ? (
              <div style={{
                ...styles.card,
                padding: '28px',
                border: `1px solid ${brandColors.gold}`,
                marginBottom: '24px',
                position: 'relative',
              }}>
                {/* Gold top bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${brandColors.gold}, transparent)` }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <span style={{
                        background: 'rgba(212,175,55,0.15)',
                        border: `1px solid rgba(212,175,55,0.4)`,
                        borderRadius: '20px',
                        padding: '2px 10px',
                        color: brandColors.gold,
                        fontSize: '11px',
                        fontWeight: '700',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}>Active</span>
                      <span style={{ color: brandColors.silver, fontSize: '12px' }}>
                        Since {fmtDate(activeAssignment.assigned_at)}
                      </span>
                    </div>
                    <h3 style={{ color: brandColors.white, margin: '0 0 6px', fontSize: '20px', fontWeight: '700' }}>{activeProgram.name}</h3>
                    {activeProgram.description && (
                      <p style={{ color: brandColors.silver, margin: 0, fontSize: '13px', lineHeight: '1.5' }}>{activeProgram.description}</p>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                    <button
                      onClick={() => editingProgram?.id === activeProgram.id
                        ? (setEditingProgram(null) || setProgramDetails(null))
                        : viewProgram(activeProgram)
                      }
                      style={{
                        padding: '10px 20px',
                        backgroundColor: editingProgram?.id === activeProgram.id ? brandColors.lightGray : brandColors.gold,
                        color: editingProgram?.id === activeProgram.id ? brandColors.white : brandColors.darkGray,
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                      }}
                    >
                      {editingProgram?.id === activeProgram.id ? 'Done' : 'View & Edit'}
                    </button>
                    <button
                      onClick={() => { setShowAssignPanel(p => !p); setSelectedProgram(''); setShowAdvanceWeek(false); }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'transparent',
                        color: brandColors.white,
                        border: `1px solid ${brandColors.lightGray}`,
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      Replace Program
                    </button>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setShowOverflowMenu(p => !p)}
                        style={{
                          padding: '10px 16px',
                          backgroundColor: 'transparent',
                          color: brandColors.silver,
                          border: `1px solid rgba(255,255,255,0.15)`,
                          borderRadius: '8px',
                          fontSize: '18px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          letterSpacing: '2px',
                          lineHeight: 1,
                        }}
                        title="More options"
                      >
                        ···
                      </button>
                      {showOverflowMenu && (
                        <>
                        <div onClick={() => setShowOverflowMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
                        <div style={{
                          position: 'absolute',
                          bottom: 'calc(100% + 6px)',
                          right: 0,
                          background: theme.surface1,
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: '8px',
                          padding: '6px',
                          zIndex: 200,
                          minWidth: '160px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        }}>
                          <button
                            onClick={() => { setShowOverflowMenu(false); openAdvanceWeek(activeProgram.id, activeAssignment?.current_week || 1); }}
                            style={{
                              display: 'block', width: '100%', padding: '10px 14px',
                              background: 'transparent', border: 'none', borderRadius: '8px',
                              color: brandColors.gold, fontSize: '13px', fontWeight: '600',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.1)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Set Week
                          </button>
                          <button
                            onClick={() => {
                              setShowOverflowMenu(false);
                              const next = !showDeleteSession;
                              setShowDeleteSession(next);
                              setShowAdvanceWeek(false);
                              setMoveTarget({});
                              if (next) {
                                loadClientSessions(selectedClient.user_id, activeProgram.id);
                                loadProgramDays(activeProgram.id);
                              }
                            }}
                            style={{
                              display: 'block', width: '100%', padding: '10px 14px',
                              background: 'transparent', border: 'none', borderRadius: '8px',
                              color: theme.danger, fontSize: '13px', fontWeight: '600',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,69,58,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Delete Session
                          </button>
                          <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '4px 6px' }} />
                          <button
                            onClick={() => { setShowOverflowMenu(false); setConfirmDialog({ isOpen: true, programId: activeProgram.id, userId: selectedClient.user_id }); }}
                            style={{
                              display: 'block', width: '100%', padding: '10px 14px',
                              background: 'transparent', border: 'none', borderRadius: '8px',
                              color: brandColors.red, fontSize: '13px', fontWeight: '600',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,69,58,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Unassign
                          </button>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Delete Session Panel ── */}
                {showDeleteSession && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: 'rgba(255,69,58,0.06)',
                    border: '1px solid rgba(255,69,58,0.25)',
                    borderRadius: '8px',
                  }}>
                    <p style={{ color: theme.danger, fontSize: '12px', margin: '0 0 12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Delete a workout session — this cannot be undone
                    </p>
                    {loadingSessions ? (
                      <p style={{ color: brandColors.silver, fontSize: '13px', margin: 0 }}>Loading sessions...</p>
                    ) : clientSessions.length === 0 ? (
                      <p style={{ color: brandColors.silver, fontSize: '13px', margin: 0 }}>No sessions found for this program.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {clientSessions.map(s => {
                          const weekNum = s.program_days?.program_weeks?.week_number;
                          const dayNum  = s.program_days?.day_number;
                          const date    = new Date(s.started_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                          const isMoving = moveTarget[s.id] !== undefined;
                          return (
                            <div key={s.id} style={{
                              padding: '10px 12px',
                              background: 'rgba(255,255,255,0.04)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255,255,255,0.08)',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: brandColors.white, fontSize: '13px' }}>
                                  {weekNum ? `Week ${weekNum}` : '?'}{dayNum ? `, Day ${dayNum}` : ''} — {date}
                                </span>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    onClick={() => setMoveTarget(prev => ({ ...prev, [s.id]: prev[s.id] !== undefined ? undefined : '' }))}
                                    style={{
                                      padding: '5px 12px',
                                      backgroundColor: 'transparent',
                                      color: brandColors.gold,
                                      border: '1px solid rgba(212,175,55,0.4)',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Move
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSession(s.id, selectedClient.user_id, activeProgram.id)}
                                    disabled={deletingSession === s.id}
                                    style={{
                                      padding: '5px 12px',
                                      backgroundColor: 'transparent',
                                      color: theme.danger,
                                      border: '1px solid rgba(255,69,58,0.4)',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      cursor: deletingSession === s.id ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {deletingSession === s.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                </div>
                              </div>
                              {isMoving && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                                  <select
                                    value={moveTarget[s.id] || ''}
                                    onChange={e => setMoveTarget(prev => ({ ...prev, [s.id]: e.target.value }))}
                                    style={{
                                      flex: 1,
                                      padding: '6px 10px',
                                      background: brandColors.darkGray,
                                      border: `1px solid ${brandColors.lightGray}`,
                                      borderRadius: '8px',
                                      color: brandColors.white,
                                      fontSize: '13px',
                                    }}
                                  >
                                    <option value="">Move to...</option>
                                    {programDays.map(d => (
                                      <option key={d.id} value={d.id}>
                                        Week {d.program_weeks?.week_number}, Day {d.day_number}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleMoveSession(s.id, moveTarget[s.id], selectedClient.user_id, activeProgram.id)}
                                    disabled={!moveTarget[s.id] || movingSession === s.id}
                                    style={{
                                      padding: '6px 14px',
                                      backgroundColor: moveTarget[s.id] ? brandColors.gold : brandColors.lightGray,
                                      color: brandColors.darkGray,
                                      border: 'none',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      cursor: moveTarget[s.id] ? 'pointer' : 'not-allowed',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {movingSession === s.id ? 'Moving...' : 'Confirm Move'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Set Week Panel ── */}
                {showAdvanceWeek && (() => {
                  const currentWeek = activeAssignment?.current_week || 1;
                  const weekOptions = availableWeeks.length > 0 ? availableWeeks : [currentWeek];
                  return (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      background: 'rgba(212,175,55,0.07)',
                      border: `1px solid rgba(212,175,55,0.3)`,
                      borderRadius: '8px',
                    }}>
                      <p style={{ color: brandColors.silver, fontSize: '12px', margin: '0 0 10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Move client to week — currently on Week {currentWeek}
                      </p>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                        <select
                          value={advanceWeekTarget}
                          onChange={e => setAdvanceWeekTarget(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            background: brandColors.darkGray,
                            border: `1px solid ${brandColors.lightGray}`,
                            borderRadius: '8px',
                            color: brandColors.white,
                            fontSize: '14px',
                          }}
                        >
                          <option value="">Select week...</option>
                          {weekOptions.map(w => (
                            <option key={w} value={w}>Week {w}{w === currentWeek ? ' (current)' : ''}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleAdvanceWeek(selectedClient.user_id, activeProgram.id, parseInt(advanceWeekTarget))}
                          disabled={!advanceWeekTarget || parseInt(advanceWeekTarget) === currentWeek || advancingWeek}
                          style={{
                            padding: '8px 18px',
                            backgroundColor: !advanceWeekTarget || parseInt(advanceWeekTarget) === currentWeek ? brandColors.lightGray : brandColors.gold,
                            color: brandColors.darkGray,
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '700',
                            cursor: !advanceWeekTarget || parseInt(advanceWeekTarget) === currentWeek ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {advancingWeek ? 'Saving...' : 'Confirm'}
                        </button>
                      </div>
                      <div style={{ borderTop: `1px solid rgba(255,255,255,0.08)`, paddingTop: '10px' }}>
                        <p style={{ color: brandColors.silver, fontSize: '11px', margin: '0 0 8px' }}>
                          Week {weekOptions[weekOptions.length - 1] + 1} not in list? Generate it directly:
                        </p>
                        <button
                          onClick={() => handleGenerateNextWeek(activeProgram.id, selectedClient.user_id)}
                          disabled={generatingNextWeek}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: 'transparent',
                            color: brandColors.gold,
                            border: `1px solid rgba(212,175,55,0.4)`,
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: generatingNextWeek ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {generatingNextWeek ? 'Generating...' : `Generate & move to Week ${weekOptions[weekOptions.length - 1] + 1}`}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ ...styles.card, padding: '28px', marginBottom: '24px', textAlign: 'center' }}>
                <div style={{ color: brandColors.silver, fontSize: '40px', marginBottom: '12px' }}>📋</div>
                <p style={{ color: brandColors.silver, margin: '0 0 16px' }}>No active program assigned</p>
                <button
                  onClick={() => setShowAssignPanel(true)}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: brandColors.gold,
                    color: brandColors.darkGray,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  Assign Program
                </button>
              </div>
            )}

            {/* ── Program Editor ── */}
            {editingProgram && (
              <div style={{ ...styles.card, padding: '24px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h3 style={{ color: brandColors.white, margin: '0 0 2px', fontSize: '15px', fontWeight: '700' }}>
                      {editingProgram.name}
                    </h3>
                    <p style={{ color: brandColors.silver, margin: 0, fontSize: '12px' }}>
                      Click any exercise to swap it · Changes save when you leave a box · Avg = last 4 weeks
                    </p>
                    {editorWeek.note && (
                      <p style={{ color: brandColors.white, margin: '6px 0 0', fontSize: '13px' }}>{editorWeek.note}</p>
                    )}
                    {(editorWeek.choices || []).length > 1 && (
                      <div role="group" aria-label="Week to edit" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {editorWeek.choices.map((w) => (
                          <button
                            key={w}
                            onClick={() => w !== editorWeek.week && onViewWeek?.(w)}
                            aria-pressed={w === editorWeek.week}
                            style={{
                              padding: '6px 12px', minHeight: 32, borderRadius: '16px', fontSize: '12px', cursor: 'pointer',
                              border: `1px solid ${w === editorWeek.week ? brandColors.gold : brandColors.lightGray}`,
                              backgroundColor: w === editorWeek.week ? 'rgba(212,175,55,0.15)' : 'transparent',
                              color: w === editorWeek.week ? brandColors.gold : brandColors.silver,
                            }}
                          >
                            Week {w}{w === editorWeek.current ? ' (now)' : ''}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setEditingProgram(null); setProgramDetails(null); setExerciseAvgs({}); }}
                    style={{ background: 'transparent', border: 'none', color: brandColors.silver, fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
                  >
                    ✕
                  </button>
                </div>

                {!programDetails && programLoadFailed ? (
                  <div role="alert" style={{ textAlign: 'center', padding: '32px', color: brandColors.silver }}>
                    Couldn't load this program, so nothing here can be edited yet.{' '}
                    <button onClick={retryProgramLoad} style={{ background: 'none', border: 'none', color: brandColors.white, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', minHeight: 44 }}>
                      Try again
                    </button>
                  </div>
                ) : !programDetails ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: brandColors.silver }}>Loading...</div>
                ) : (() => {
                  // The client's current week: the only week loaded (Codex's review 023, R1)
                  const templateWeek = programDetails[0];
                  if (!templateWeek) return editorWeek.note ? null : <div style={{ color: brandColors.silver, fontSize: '13px' }}>No program structure found.</div>;

                  const renderExerciseRow = (exId, exName, itemId, sets, reps, restSeconds, onSetsBlur, onRepsBlur, onRestBlur, onSwap, currentExercise, onDelete, isBlock, programExerciseId) => {
                    const avg = exerciseAvgs[exId];
                    const isSwapping = swappingExercise?.peId === itemId;
                    const suggested = isSwapping ? getSuggestedExercises(swappingExercise.currentExercise) : [];
                    const filteredSwap = suggested.filter(ex => {
                      const matchesSearch = ex.name.toLowerCase().includes(swapSearch.toLowerCase());
                      const matchesCategory = swapCategoryFilter === 'All' || ex.movement_category === swapCategoryFilter;
                      const matchesSecondary = swapSecondaryFilter === 'All' ||
                        (ex.secondary_muscles && ex.secondary_muscles.includes(swapSecondaryFilter));
                      return matchesSearch && matchesCategory && matchesSecondary;
                    });

                    const exerciseType = currentExercise?.exercise_type || 'resistance';
                    const durationUnit = currentExercise?.duration_unit || 'minutes';
                    const isTimeBased = exerciseType === 'cardio' || exerciseType === 'isometric' || exerciseType === 'stretch';

                    const isFlagged = programExerciseId ? clientFlags.some(f => f.program_exercise_id === programExerciseId) : false;
                    // The client's swaps away from this exercise, if any (see the Client swaps panel above).
                    const swapGroup = (clientSwaps || []).find(g => g.originalId != null && Number(g.originalId) === Number(exId));

                    return (
                      <div key={itemId} style={{ position: 'relative' }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto 64px 64px 64px 28px',
                          gap: '8px', alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: `1px solid rgba(255,255,255,0.04)`,
                        }}>
                          {/* Exercise name — click to swap */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                            <span
                              onClick={() => { setSwappingExercise({ peId: itemId, currentExercise, isBlock: !!isBlock }); setSwapSearch(''); setShowSwapAll(false); resetSwapFilters(); }}
                              style={{ color: brandColors.white, fontSize: '13px', cursor: 'pointer' }}
                              title="Click to swap template"
                            >
                              {exName || '—'} <span style={{ color: brandColors.silver, fontSize: '11px' }}>✎</span>
                              {isFlagged && (
                                <span style={{ color: brandColors.red, fontSize: 13, marginLeft: 4 }} title="Client flagged this exercise">🚩</span>
                              )}
                              {swapGroup && (
                                <span
                                  style={{ color: swapGroup.hasDiscomfort ? brandColors.red : brandColors.gold, fontSize: 11, fontWeight: 700, marginLeft: 6 }}
                                  title={`Client swapped this ${swapGroup.count === 1 ? 'once' : `${swapGroup.count} times`} in the last ${SWAP_HISTORY_DAYS} days, most often for ${swapGroup.replacements[0]?.name}`}
                                >
                                  ⇄ {swapGroup.count}
                                </span>
                              )}
                            </span>
                          </div>
                          {/* 4-week avg chip */}
                          {avg ? (
                            <span style={{
                              background: 'rgba(212,175,55,0.12)',
                              border: `1px solid rgba(212,175,55,0.25)`,
                              borderRadius: '20px', padding: '2px 8px',
                              color: brandColors.gold, fontSize: '11px', whiteSpace: 'nowrap',
                            }}>
                              ~{avg.avgWeight}kg × {avg.avgReps}
                            </span>
                          ) : <span />}
                          {/* Sets */}
                          <SaveOnBlurInput aria-label={exName + ' sets'} type="number" defaultValue={sets} min={1} onBlur={(e) => { if (String(e.target.value) !== String(sets ?? '')) return onSetsBlur(e); }}
                            style={{ padding: '5px', backgroundColor: brandColors.darkGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px', color: brandColors.white, fontSize: '12px', textAlign: 'center', width: '100%' }} />
                          {/* Reps / Duration */}
                          <div>
                            <SaveOnBlurInput aria-label={exName + ' reps'} type="text" defaultValue={reps} onBlur={(e) => { if (String(e.target.value) !== String(reps ?? '')) return onRepsBlur(e); }}
                              style={{ padding: '5px', backgroundColor: brandColors.darkGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px', color: brandColors.white, fontSize: '12px', textAlign: 'center', width: '100%', boxSizing: 'border-box' }} />
                            {isTimeBased && currentExercise?.id && (
                              // The unit belongs to the exercise in the library, for every program; it is
                              // changed there, not here (Codex's review 023, R7: this switch changed it for all)
                              <div title="Set in the exercise library" style={{ marginTop: '3px', fontSize: '11px', color: brandColors.silver, textAlign: 'center' }}>
                                {durationUnit === 'seconds' ? 'seconds' : 'minutes'}
                              </div>
                            )}
                          </div>
                          {/* Rest. Belongs to the block, so only its last row carries the box (no handler = no box).
                              Keyed on the saved value so that after a save it redraws showing what was stored:
                              "2m" is read as 2 seconds and comes back as "2s". */}
                          {onRestBlur ? (
                            <SaveOnBlurInput aria-label={exName + ' rest seconds'} type="text" inputKey={restSeconds} defaultValue={restSeconds != null ? `${restSeconds}s` : ''} placeholder="—" onBlur={(e) => { if (e.target.value !== (restSeconds != null ? `${restSeconds}s` : '')) return onRestBlur(e); }}
                              title="Rest after this block, in seconds"
                              style={{ padding: '5px', backgroundColor: brandColors.darkGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px', color: brandColors.white, fontSize: '12px', textAlign: 'center', width: '100%' }} />
                          ) : <span />}
                          {/* Delete */}
                          <button
                            onClick={onDelete}
                            title="Remove exercise"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: brandColors.silver, fontSize: '14px', padding: '2px', lineHeight: 1, transition: 'color 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.color = brandColors.red}
                            onMouseLeave={e => e.currentTarget.style.color = brandColors.silver}
                          >
                            🗑
                          </button>
                        </div>

                        {/* Swap Exercise Modal — same format as Program Builder's exercise picker */}
                        {isSwapping && (
                          <div
                            onClick={() => { setSwappingExercise(null); setSwapSearch(''); setPendingSwapEx(null); setSwapReason(''); setShowSwapAll(false); resetSwapFilters(); }}
                            style={{
                              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                              backgroundColor: 'rgba(0,0,0,0.8)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              zIndex: 1000,
                            }}
                          >
                            <div
                              onClick={e => e.stopPropagation()}
                              style={{
                                backgroundColor: brandColors.mediumGray,
                                borderRadius: '14px',
                                width: '90%', maxWidth: '600px', maxHeight: '80vh',
                                overflow: 'hidden',
                                display: 'flex', flexDirection: 'column',
                              }}
                            >
                              {/* Header */}
                              <div style={{ padding: '20px', borderBottom: `1px solid ${brandColors.lightGray}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                  <h3 style={{ color: brandColors.gold, margin: 0, fontSize: '18px' }}>Swap Exercise</h3>
                                  <div style={{ color: brandColors.silver, fontSize: '11px', marginTop: '4px' }}>
                                    Replacing: {currentExercise?.name || exName}
                                  </div>
                                </div>
                                <button
                                  onClick={() => { setSwappingExercise(null); setSwapSearch(''); setPendingSwapEx(null); setSwapReason(''); setShowSwapAll(false); resetSwapFilters(); }}
                                  style={{ backgroundColor: 'transparent', border: 'none', color: brandColors.silver, fontSize: '24px', cursor: 'pointer' }}
                                >×</button>
                              </div>

                              {/* Search and Filters */}
                              <div style={{ padding: '15px', borderBottom: `1px solid ${brandColors.lightGray}` }}>
                                <input
                                  autoFocus
                                  type="text"
                                  placeholder="Search exercises..."
                                  value={swapSearch}
                                  onChange={e => { setSwapSearch(e.target.value); setPendingSwapEx(null); setSwapReason(''); }}
                                  style={{ width: '100%', padding: '12px', backgroundColor: brandColors.darkGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px', color: 'white', fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' }}
                                />
                                <div style={{ marginBottom: '10px' }}>
                                  <label style={{ color: brandColors.silver, fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                                    Movement Category
                                  </label>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {getSwapMovementCategories().map(category => (
                                      <button
                                        key={category}
                                        onClick={() => { setSwapCategoryFilter(category); setSwapSecondaryFilter('All'); }}
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: swapCategoryFilter === category ? brandColors.gold : brandColors.darkGray,
                                          border: `1px solid ${brandColors.lightGray}`,
                                          borderRadius: '20px',
                                          color: swapCategoryFilter === category ? brandColors.darkGray : brandColors.silver,
                                          fontSize: '12px', cursor: 'pointer',
                                          fontWeight: swapCategoryFilter === category ? 'bold' : 'normal',
                                        }}
                                      >{category}</button>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <label style={{ color: brandColors.silver, fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                                    Secondary Muscle
                                  </label>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxHeight: '80px', overflowY: 'auto' }}>
                                    {getSwapSecondaryMuscles().map(muscle => (
                                      <button
                                        key={muscle}
                                        onClick={() => setSwapSecondaryFilter(muscle)}
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: swapSecondaryFilter === muscle ? brandColors.gold : brandColors.darkGray,
                                          border: `1px solid ${brandColors.lightGray}`,
                                          borderRadius: '20px',
                                          color: swapSecondaryFilter === muscle ? brandColors.darkGray : brandColors.silver,
                                          fontSize: '12px', cursor: 'pointer',
                                          fontWeight: swapSecondaryFilter === muscle ? 'bold' : 'normal',
                                        }}
                                      >{muscle}</button>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Exercise List */}
                              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px' }}>
                                {filteredSwap.length === 0 ? (
                                  <div style={{ textAlign: 'center', color: brandColors.silver, padding: '20px' }}>
                                    No exercises found. Try a different search or filter.
                                  </div>
                                ) : (
                                  filteredSwap.map(ex => {
                                    const isPending = pendingSwapEx?.id === ex.id;
                                    return (
                                      <div
                                        key={ex.id}
                                        onClick={() => { setPendingSwapEx({ id: ex.id, name: ex.name }); setSwapReason(''); }}
                                        onMouseEnter={() => setSwapHoveredId(ex.id)}
                                        onMouseLeave={() => setSwapHoveredId(null)}
                                        onTouchStart={() => setSwapHoveredId(ex.id)}
                                        style={{
                                          padding: '12px 15px',
                                          backgroundColor: isPending ? 'rgba(212,175,55,0.12)' : brandColors.darkGray,
                                          borderRadius: '8px',
                                          marginBottom: '8px',
                                          cursor: 'pointer',
                                          border: isPending ? `1px solid ${brandColors.gold}` : `1px solid ${brandColors.lightGray}`,
                                          transition: 'all 0.2s',
                                        }}
                                      >
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                          {/* GIF thumbnail — only fetched once the row is hovered/tapped
                                              or selected. Rendering an <img> for every row on open pulled
                                              the full-size animation (1-5 MB each) for the entire filtered
                                              list at once, saturating the connection and stalling the
                                              block_items save requests. */}
                                          {ex.gif_url && (swapHoveredId === ex.id || isPending) ? (
                                            <ExerciseDemo
                                              gifUrl={ex.gif_url}
                                              alt={ex.name}
                                              style={{
                                                width: (swapHoveredId === ex.id || isPending) ? '80px' : '48px',
                                                height: (swapHoveredId === ex.id || isPending) ? '80px' : '48px',
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                flexShrink: 0,
                                                transition: 'width 0.2s, height 0.2s',
                                              }}
                                            />
                                          ) : (
                                            <div style={{
                                              width: '48px', height: '48px', borderRadius: '8px',
                                              backgroundColor: brandColors.lightGray, flexShrink: 0,
                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              fontSize: '20px',
                                            }}>
                                              {ex.gif_url ? '▶' : '💪'}
                                            </div>
                                          )}
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ color: 'white', fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                              {ex.score > 0 && <span style={{ color: brandColors.gold, fontSize: '12px' }} title="Similar to current exercise">★</span>}
                                              {ex.name}
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                              {ex.movement_category && (
                                                <span style={{
                                                  backgroundColor: brandColors.gold,
                                                  color: brandColors.darkGray,
                                                  padding: '2px 8px',
                                                  borderRadius: '8px',
                                                  fontSize: '11px',
                                                  fontWeight: 700
                                                }}>
                                                  {ex.movement_category}
                                                </span>
                                              )}
                                              {ex.secondary_muscles && (
                                                <span style={{
                                                  backgroundColor: brandColors.lightGray,
                                                  color: brandColors.silver,
                                                  padding: '2px 8px',
                                                  borderRadius: '8px',
                                                  fontSize: '11px'
                                                }}>
                                                  {ex.secondary_muscles}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>

                              {/* Confirm swap + note */}
                              {pendingSwapEx && (
                                <div style={{ padding: '15px', borderTop: `1px solid rgba(212,175,55,0.25)`, background: 'rgba(212,175,55,0.05)', flexShrink: 0 }}>
                                  <div style={{ fontSize: '12px', color: brandColors.gold, fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                    Swap to: {pendingSwapEx.name}
                                  </div>
                                  <input
                                    value={swapReason}
                                    onChange={e => setSwapReason(e.target.value)}
                                    placeholder="Note (optional) — e.g. shoulder issue, no cables available"
                                    style={{
                                      width: '100%', padding: '8px 10px', marginBottom: '10px', boxSizing: 'border-box',
                                      backgroundColor: brandColors.darkGray,
                                      border: `1px solid ${brandColors.lightGray}`,
                                      borderRadius: '8px', color: brandColors.white,
                                      fontSize: '13px', outline: 'none',
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                      onClick={() => { setPendingSwapEx(null); setSwapReason(''); }}
                                      style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'none', border: `1px solid ${brandColors.lightGray}`, color: brandColors.silver, fontSize: '13px', cursor: 'pointer' }}
                                    >← Back</button>
                                    <button
                                      onClick={() => { if (swapSaving) return; onSwap(pendingSwapEx.id); resetSwapFilters(); }}
                                      disabled={swapSaving}
                                      style={{
                                        flex: 2, padding: '10px', borderRadius: '8px',
                                        background: swapSaving ? brandColors.lightGray : brandColors.gold,
                                        border: 'none', color: brandColors.darkGray, fontSize: '14px', fontWeight: '700',
                                        cursor: swapSaving ? 'default' : 'pointer', opacity: swapSaving ? 0.7 : 1,
                                      }}
                                    >{swapSaving ? 'Swapping…' : 'Confirm swap'}</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {(templateWeek.days || []).map((day) => (
                        <div key={day.id} style={{ backgroundColor: brandColors.darkGray, borderRadius: '8px', padding: '16px', border: `1px solid rgba(255,255,255,0.06)` }}>
                          <div style={{ color: brandColors.white, fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>
                            Day {day.day_number}{day.name ? ` — ${day.name}` : ''}
                          </div>

                          {/* Column headers */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 64px 64px 64px 28px', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ color: brandColors.silver, fontSize: '11px', textTransform: 'uppercase' }}>Exercise</span>
                            <span style={{ color: brandColors.silver, fontSize: '11px', textTransform: 'uppercase' }}>4wk avg</span>
                            {['Sets', 'Reps/Dur', 'Rest'].map(l => (
                              <span key={l} style={{ color: brandColors.silver, fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>{l}</span>
                            ))}
                            <span />
                          </div>

                          {/* Blocks layout */}
                          {day.blocks && day.blocks.length > 0 ? (
                            day.blocks.map((block) => (
                              <div key={block.id} style={{ marginBottom: '8px' }}>
                                {block.name && (
                                  <div style={{ color: brandColors.gold, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', marginTop: '8px' }}>
                                    {block.name}
                                  </div>
                                )}
                                {(block.items || []).map((item, itemIndex, blockItems) =>
                                  renderExerciseRow(
                                    item.exercise_id, item.exercise?.name, item.id,
                                    item.sets, item.reps, block.rest_seconds,
                                    (e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0) return updateBlockItem(item.id, 'sets', v, item.exercise_id, block.block_order, day.day_number); },
                                    (e) => updateBlockItem(item.id, 'reps', e.target.value, item.exercise_id, block.block_order, day.day_number),
                                    // Rest is taken after the whole block (the client app shows it that way), so in a
                                    // superset only the last exercise carries the box. An untouched box saves nothing.
                                    // An out-of-range number is still sent: the database refuses it, which shows the
                                    // error toast, where filtering it here would make the box quietly not save.
                                    itemIndex === blockItems.length - 1
                                      ? (e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v !== block.rest_seconds) return updateBlockRest(block.id, v, item.exercise_id, block.block_order, day.day_number); }
                                      : null,
                                    (newId) => swapBlockExercise(item.id, item.exercise_id, block.block_order, day.day_number, newId),
                                    item.exercise,
                                    () => deleteBlockItem(item.id, item.exercise_id, block.block_order, day.day_number),
                                    true,
                                    item.id,
                                  )
                                )}
                              </div>
                            ))
                          ) : (
                            /* Legacy exercises */
                            <div>
                              {(day.exercises || []).length === 0
                                ? <span style={{ color: brandColors.silver, fontSize: '12px' }}>No exercises</span>
                                : (day.exercises || []).map(pe =>
                                    renderExerciseRow(
                                      pe.exercise_id, pe.exercise?.name, pe.id,
                                      pe.sets, pe.reps, null,
                                      (e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0) return updateExercise(pe.id, 'sets', v, pe.exercise_id, day.day_number); },
                                      (e) => updateExercise(pe.id, 'reps', e.target.value, pe.exercise_id, day.day_number),
                                      null, // legacy rows have nowhere to store rest (no column on program_exercises), so no box
                                      (newId) => swapExercise(pe.id, pe.exercise_id, day.day_number, newId),
                                      pe.exercise,
                                      () => deleteProgramExercise(pe.id, pe.exercise_id, day.day_number),
                                      false,
                                      pe.id,
                                    )
                                  )
                              }
                            </div>
                          )}

                          {/* Add Exercise button + picker */}
                          {(() => {
                            const programIsBlockBased = (templateWeek.days || []).some(d => d.blocks && d.blocks.length > 0);
                            const isAddingThisDay = addingExercise?.dayId === day.id;
                            const filtered = exerciseLibrary.filter(ex =>
                              ex.name.toLowerCase().includes(addExerciseSearch.toLowerCase())
                            );
                            return (
                              <div style={{ position: 'relative', marginTop: '8px' }}>
                                <button
                                  onClick={() => {
                                    setAddingExercise({ dayId: day.id, dayNumber: day.day_number, isBlock: programIsBlockBased });
                                    setAddExerciseSearch('');
                                  }}
                                  style={{
                                    background: 'none',
                                    border: `1px dashed rgba(212,175,55,0.35)`,
                                    borderRadius: '8px',
                                    color: brandColors.gold,
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                    width: '100%',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(212,175,55,0.7)'}
                                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(212,175,55,0.35)'}
                                >
                                  + Add Exercise
                                </button>
                                {isAddingThisDay && (
                                  <div style={{
                                    position: 'absolute', zIndex: 200, top: '100%', left: 0,
                                    width: '300px', maxHeight: '300px', overflowY: 'auto',
                                    backgroundColor: brandColors.mediumGray,
                                    border: `1px solid ${brandColors.gold}`,
                                    borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                                    marginTop: '4px',
                                  }}>
                                    <div style={{ display: 'flex', gap: '6px', padding: '8px', borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
                                      <input
                                        autoFocus
                                        value={addExerciseSearch}
                                        onChange={e => setAddExerciseSearch(e.target.value)}
                                        placeholder="Search exercises..."
                                        style={{ flex: 1, padding: '5px 8px', backgroundColor: brandColors.darkGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: '4px', color: brandColors.white, fontSize: '12px', outline: 'none' }}
                                      />
                                      <button
                                        onClick={() => { setAddingExercise(null); setAddExerciseSearch(''); }}
                                        style={{ background: 'none', border: 'none', color: brandColors.silver, fontSize: '16px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
                                      >✕</button>
                                    </div>
                                    {filtered.length === 0 ? (
                                      <div style={{ padding: '12px 10px', color: brandColors.silver, fontSize: '12px', textAlign: 'center' }}>No exercises found</div>
                                    ) : (
                                      filtered.map(ex => (
                                        <div
                                          key={ex.id}
                                          onClick={() => { if (addExerciseSaving) return; addExerciseToDay(day.id, day.day_number, addingExercise.isBlock, ex.id); }}
                                          style={{
                                            padding: '7px 10px', fontSize: '12px', color: brandColors.white, borderLeft: '3px solid transparent',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            cursor: addExerciseSaving ? 'default' : 'pointer', opacity: addExerciseSaving ? 0.5 : 1,
                                          }}
                                          onMouseEnter={e => { if (!addExerciseSaving) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
                                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                          <span>{ex.name}</span>
                                          {ex.movement_category && (
                                            <span style={{ fontSize: '11px', color: brandColors.gold, background: 'rgba(212,175,55,0.15)', borderRadius: '8px', padding: '1px 6px' }}>
                                              {ex.movement_category}
                                            </span>
                                          )}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                      {addDay && editorWeek.current != null && !editorWeek.legacy && (
                        <AddDayPanel
                          days={templateWeek.days || []}
                          fromWeek={Math.max(editorWeek.week ?? 0, editorWeek.current + 1)}
                          onAdd={addDay}
                          saving={addDaySaving}
                        />
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Assign / Replace Panel ── */}
            {showAssignPanel && (
              <div style={{
                ...styles.card,
                padding: '24px',
                marginBottom: '24px',
                border: `1px solid rgba(212,175,55,0.2)`,
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: brandColors.white, margin: '0 0 4px', fontSize: '15px', fontWeight: '600' }}>
                    {activeProgram ? 'Replace Active Program' : 'Assign Program'}
                  </h3>
                  <p style={{ color: brandColors.green, margin: 0, fontSize: '12px' }}>
                    All workout stats are preserved — switching programs never deletes historical data.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <select
                    value={selectedProgram}
                    onChange={(e) => setSelectedProgram(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: brandColors.darkGray,
                      border: `1px solid ${brandColors.lightGray}`,
                      borderRadius: '8px',
                      color: brandColors.white,
                      fontSize: '14px',
                    }}
                  >
                    <option value="">Select a program...</option>
                    {getAssignableProgramGroups(activeProgram?.id).map(group => (
                      <optgroup key={group.key} label={group.label}>
                        {group.options.map(option => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    onClick={() => selectedProgram && assignProgram(selectedClient.user_id, selectedProgram)}
                    disabled={!selectedProgram || !!assigningTo}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: selectedProgram && !assigningTo ? brandColors.gold : brandColors.lightGray,
                      color: selectedProgram && !assigningTo ? brandColors.darkGray : brandColors.silver,
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: selectedProgram && !assigningTo ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {assigningTo ? 'Assigning...' : 'Confirm Assign'}
                  </button>
                  <button
                    onClick={() => { setShowAssignPanel(false); setSelectedProgram(''); }}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      color: brandColors.silver,
                      border: `1px solid ${brandColors.lightGray}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── Program History ── */}
            {pastAssignments.length > 0 && (
              <div style={{ ...styles.card, padding: '24px' }}>
                <h3 style={{ color: brandColors.white, margin: '0 0 4px', fontSize: '15px', fontWeight: '600' }}>Program History</h3>
                <p style={{ color: brandColors.silver, margin: '0 0 20px', fontSize: '12px' }}>Stats from all previous programs are preserved and included in performance analytics.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {pastAssignments.map((a, i) => (
                    <div key={a.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                      gap: '12px',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: brandColors.white, fontSize: '13px', fontWeight: '600' }}>
                          {a.program?.name || `Program #${a.program_id}`}
                        </div>
                        <div style={{ color: brandColors.silver, fontSize: '11px', marginTop: '2px' }}>
                          {fmtDate(a.assigned_at)} → {fmtDate(a.ended_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedProgram(a.program_id);
                          setShowAssignPanel(true);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: 'transparent',
                          color: brandColors.silver,
                          border: `1px solid ${brandColors.lightGray}`,
                          borderRadius: '8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Re-assign
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default React.memo(ClientProgramsTab, shallowEqualIgnoringFunctions);
